import { Box, Flex, HStack, Text, Tooltip } from "@chakra-ui/react";
import { useLocation, useParams } from "react-router";
import { PreloadLink } from "./PreloadLink";
import { NavigationSource } from "../../types";

interface SideBarItemProps {
  icon: JSX.Element;
  buttonElement?: JSX.Element;
  to: string;
  text?: string;
  external?: boolean;
  collapsed?: boolean;
  disabled?: boolean;
}

export function SideBarItem({
  icon,
  to,
  text,
  external,
  collapsed,
  buttonElement,
  disabled = false,
}: SideBarItemProps) {
  const { channel } = useParams();
  const location = useLocation();

  const isCreatorsPage = location.pathname.startsWith("/creators");

  const isReleasesPage = location.pathname.startsWith("/books");

  const isBundlesPage = location.pathname.startsWith("/bundles");

  // Check if this item should be active
  let itemActive = false;

  // Exact path match
  if (location.pathname === to) {
    itemActive = true;
  }
  // Special case for home when channel is "gc"
  else if (to === "/" && channel === "gc") {
    itemActive = true;
  }
  // Special cases for creators pages
  else if (to === "/browse" && isCreatorsPage) {
    itemActive = true;
  } else if (to === "/browse" && (isReleasesPage || isBundlesPage)) {
    itemActive = true;
  }
  // For non-channel routes, check if current path starts with item path
  else if (
    !to.includes("/channel/") &&
    to !== "/" &&
    (location.pathname === to || location.pathname.startsWith(to + "/"))
  ) {
    itemActive = true;
  }
  // Handle expanded content screen (/cms/:sectionId) based on navigation source
  else if (location.pathname.startsWith("/cms/")) {
    const navigationSource = location.state as NavigationSource | null;
    if (navigationSource?.sourceChannelSlug) {
      // Check if this sidebar item matches the source channel
      if (to.includes(`/channel/${navigationSource.sourceChannelSlug}`)) {
        itemActive = true;
      }
      // Special case for originals channel
      else if (
        to === "/channel/originals" &&
        navigationSource.sourceChannelSlug === "originals"
      ) {
        itemActive = true;
      }
    } else if (navigationSource?.sourcePath) {
      // Fallback: check if this sidebar item matches the source path
      if (
        to === navigationSource.sourcePath ||
        navigationSource.sourcePath.startsWith(to)
      ) {
        itemActive = true;
      }
    }
    // Additional fallback for home path
    else if (
      to === "/" &&
      (!navigationSource?.sourceChannelSlug ||
        navigationSource.sourceChannelSlug === "gc")
    ) {
      itemActive = true;
    }
  }

  return (
    <PreloadLink
      to={to}
      name={text}
      reloadDocument={external}
      style={{
        width: "100%",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.7 : 1,
      }}
      aria-disabled={disabled}
    >
      <Tooltip
        variant="navbar"
        label={text}
        aria-label={`Navigate to ${text} page`}
        placement="right"
        isDisabled={!collapsed || !!itemActive}
        gutter={4}
      >
        <HStack alignItems="center" w="full" role="group">
          <Box
            display="flex"
            width={collapsed ? "auto" : "full"}
            alignItems="center"
            transition="all .1s linear"
            boxShadow={
              itemActive ? "0 0 0 2px var(--chakra-colors-white-10)" : undefined
            }
            borderRadius="16px"
            background={itemActive ? "transparent.white.10" : undefined}
            _groupHover={{
              color: itemActive ? undefined : "dune.700",
              background: itemActive ? undefined : "transparent.white.05",
            }}
          >
            <Flex
              w="40px"
              h="40px"
              borderRadius="16px"
              justifyContent="center"
              alignItems="center"
              transition="inherit"
              color={itemActive ? "dune.100" : "slate.950"}
              _groupHover={{
                color: itemActive ? undefined : "dune.700",
              }}
            >
              {icon}
            </Flex>
            {!collapsed ? (
              <Flex
                flexGrow={1}
                justifyContent="space-between"
                paddingRight="8px"
              >
                <Text
                  variant="navbar"
                  transition="inherit"
                  _groupHover={{
                    color: itemActive ? undefined : "dune.700",
                  }}
                  color={itemActive ? "dune.100" : "dune.700"}
                  fontSize="14px"
                >
                  {text}
                </Text>
                {buttonElement}
              </Flex>
            ) : null}
          </Box>
        </HStack>
      </Tooltip>
    </PreloadLink>
  );
}
